import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Mic } from "lucide-react";
import Gallery from "@/components/Gallery";
import { MaskedLine, Reveal, SectionTag } from "@/components/Reveal";
import { IMPACT_AREAS, IMPACT_QUOTE, SPEAKING, TOPICS, VISION } from "@/data";

const Impact = () => {
  const [active, setActive] = useState(0);

  return (
    <motion.main
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -24 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="bg-ink pt-28 text-paper"
      data-testid="impact-page"
    >
      <header className="px-6 md:px-12">
        <SectionTag no="05" label="IMPACT" dark />
        <h1 className="mt-8 font-display text-5xl font-black uppercase leading-[0.9] tracking-tighter sm:text-6xl md:text-8xl" data-testid="impact-title">
          <MaskedLine delay={0.1}><span>WHAT I HAVE</span></MaskedLine>
          <MaskedLine delay={0.25}><span className="text-ember">CONTRIBUTED</span></MaskedLine>
        </h1>
        <Reveal delay={0.3}>
          <p className="mt-8 max-w-xl font-serif text-xl italic leading-relaxed text-paper/60 md:text-2xl">
            "{IMPACT_QUOTE}"
          </p>
        </Reveal>
      </header>

      <section className="px-6 py-24 md:px-12" data-testid="impact-map">
        <SectionTag no="01" label="IMPACT MAP — KLIK UNTUK MENJELAJAH" dark />
        <div className="mt-10 flex flex-wrap gap-3">
          {IMPACT_AREAS.map((a, i) => (
            <button
              key={a.key}
              data-testid={`impact-node-${a.key.toLowerCase()}`}
              onClick={() => setActive(i)}
              className={`border px-6 py-4 font-display text-sm font-black uppercase tracking-[0.2em] transition-colors duration-300 md:text-lg ${
                active === i ? "border-ember bg-ember text-ink" : "border-white/20 text-paper/60 hover:border-paper hover:text-paper"
              }`}
            >
              {a.key}
            </button>
          ))}
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4 }}
            className="mt-10 border border-white/15 p-8 md:p-12"
            data-testid="impact-node-detail"
          >
            <p className="font-display text-xs uppercase tracking-[0.3em] text-ember">{IMPACT_AREAS[active].desc}</p>
            <ul className="mt-6 grid gap-3 md:grid-cols-2">
              {IMPACT_AREAS[active].items.map((it) => (
                <li key={it} className="flex items-center gap-3 font-display text-lg font-bold uppercase tracking-tight md:text-xl">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-ember" /> {it}
                </li>
              ))}
            </ul>
          </motion.div>
        </AnimatePresence>
      </section>

      <section className="border-t border-white/10 px-6 py-24 md:px-12" data-testid="topics-section">
        <SectionTag no="02" label="THINGS WORTH CARING ABOUT" dark />
        <div className="mt-12 flex flex-wrap gap-x-8 gap-y-4">
          {TOPICS.map((t) => (
            <span
              key={t}
              data-testid={`topic-${t.toLowerCase().replace(/\s/g, "-")}`}
              className="font-display text-2xl font-black uppercase tracking-tighter text-paper/30 transition-all duration-300 hover:scale-105 hover:text-ember md:text-4xl"
            >
              {t}
            </span>
          ))}
        </div>
        <p className="mt-8 max-w-md text-xs text-paper/40">
          Topik-topik yang saya minati, terlibat di dalamnya, dan ingin terus saya pelajari — bukan klaim keahlian profesional.
        </p>
      </section>

      <section className="relative overflow-hidden border-t border-white/10 bg-coal px-6 py-28 md:px-12" data-testid="speaking-section">
        <div className="pointer-events-none absolute -top-20 right-0 select-none font-display text-[20vw] font-black uppercase text-white/[0.03]">STAGE</div>
        <SectionTag no="03" label="THE STAGE" dark />
        <h2 className="mt-6 font-display text-4xl font-black uppercase tracking-tighter md:text-7xl" data-testid="speaking-title">
          <MaskedLine delay={0.1}><span>{SPEAKING.title}</span></MaskedLine>
        </h2>
        <div className="mt-8 flex items-start gap-5">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-ember text-ink">
            <Mic className="h-6 w-6" />
          </span>
          <p className="max-w-lg font-serif text-2xl italic leading-relaxed text-paper/70 md:text-3xl">"{SPEAKING.quote}"</p>
        </div>
        <p className="mt-6 max-w-lg text-sm text-paper/50">{SPEAKING.copy}</p>
        <div className="mt-8 flex flex-wrap gap-2">
          {SPEAKING.items.map((s) => (
            <span key={s} className="border border-white/20 px-4 py-2 font-display text-[10px] font-bold uppercase tracking-[0.25em] text-paper/70">
              {s}
            </span>
          ))}
        </div>
        <div className="mt-14">
          <Gallery items={SPEAKING.gallery} dark title="ON STAGE — DOKUMENTASI (EDITABLE)" ratio="aspect-[4/3]" />
        </div>
      </section>

      <section className="border-t border-white/10 px-6 py-28 md:px-12 md:py-40" data-testid="vision-section">
        <SectionTag no="04" label={VISION.title} dark />
        <div className="mt-12 max-w-2xl space-y-6">
          {VISION.paragraphs.map((p, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <p className="leading-relaxed text-paper/75 md:text-lg">{p}</p>
            </Reveal>
          ))}
        </div>
        <div className="mt-16">
          {VISION.statement.map((s, i) => (
            <MaskedLine key={s} delay={i * 0.12}>
              <span className={`font-display text-4xl font-black uppercase leading-[1.05] tracking-tighter sm:text-5xl md:text-7xl ${i === 2 ? "text-ember" : ""}`}>
                {s}
              </span>
            </MaskedLine>
          ))}
        </div>
        <div className="mt-20 border-t border-white/10 pt-12">
          <p className="font-display text-xs font-bold uppercase tracking-[0.35em] text-paper/50">{VISION.next.title}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            {VISION.next.areas.map((a) => (
              <span key={a} className="border border-white/20 px-5 py-3 font-display text-xs font-bold uppercase tracking-[0.2em] text-paper/80">
                {a}
              </span>
            ))}
          </div>
          <p className="mt-8 font-serif text-2xl italic text-ember md:text-3xl">"{VISION.next.closing}"</p>
        </div>
        <Reveal delay={0.15}>
          <Link
            to="/achievements"
            data-testid="impact-to-achievements"
            data-cursor="GO"
            className="mt-12 inline-block border border-paper px-8 py-4 font-display text-xs font-bold uppercase tracking-[0.3em] transition-colors duration-300 hover:bg-paper hover:text-ink"
          >
            See the milestones →
          </Link>
        </Reveal>
      </section>
    </motion.main>
  );
};

export default Impact;
